import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";

import { CACHE_TIMES } from "@/lib/queryConfig";
import { DEFAULT_BOARD_WINDOW } from "@/modules/SupplierOrders";

import type { FlowPartnerKind, PortalScope, PrinterPortalFilters } from "../types";
import { PORTAL_LANE_PAGE_SIZE, partnerPortalApi, printerPortalApi } from "./partnerPortal.api";
import { partnerPortalKeys } from "./partnerPortalQueryKeys";

/** The partner session the guard and shell read. No retry: a 403 is an answer, not a blip. */
export function usePartnerSession(partner: FlowPartnerKind, userId: number | undefined) {
  return useQuery({
    queryKey: partnerPortalKeys.session(partner, userId ?? 0),
    queryFn: () => partnerPortalApi.getSession(partner),
    enabled: userId != null,
    ...CACHE_TIMES.STANDARD,
    retry: false,
  });
}

/**
 * Whose board, New York `today` and the KPI tiles for the default window
 * (copied into the summary slice). The board, Schedule and order view all
 * read it, so it is one cached call per scope.
 */
export function usePrinterBootstrap(scope: PortalScope) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: partnerPortalKeys.bootstrap(scope),
    queryFn: async () => {
      const data = await printerPortalApi.getBootstrap(scope);
      queryClient.setQueryData(partnerPortalKeys.summary(scope, DEFAULT_BOARD_WINDOW), data.summary);
      return data;
    },
    ...CACHE_TIMES.STANDARD,
  });
}

export function usePrinterSummary(scope: PortalScope, window: PrinterPortalFilters["window"], enabled = true) {
  return useQuery({
    queryKey: partnerPortalKeys.summary(scope, window),
    queryFn: () => printerPortalApi.getSummary(scope, window),
    enabled,
    ...CACHE_TIMES.REALTIME,
  });
}

/** Lane counts with the board's filters (the API applies them). */
export function usePrinterCounts(scope: PortalScope, filters: PrinterPortalFilters, enabled = true) {
  return useQuery({
    queryKey: partnerPortalKeys.counts(scope, filters),
    queryFn: () => printerPortalApi.getCounts(scope, filters),
    enabled,
    ...CACHE_TIMES.REALTIME,
  });
}

/** One lane: its own infinite query, so a slow lane never blocks the others. */
export function usePrinterLane(scope: PortalScope, lane: string, filters: PrinterPortalFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: partnerPortalKeys.column(scope, lane, filters, PORTAL_LANE_PAGE_SIZE),
    queryFn: ({ pageParam }) =>
      printerPortalApi.getLanePage(scope, { lane, filters, cursor: pageParam, limit: PORTAL_LANE_PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    ...CACHE_TIMES.REALTIME,
  });
}

/**
 * The Schedule's month. The API returns every live part touching the range
 * (no filters); the page narrows it with the toolbar's filters.
 */
export function usePrinterSchedule(scope: PortalScope, from: string, to: string) {
  return useQuery({
    queryKey: partnerPortalKeys.schedule(scope, from, to),
    queryFn: () => printerPortalApi.getSchedule(scope, { from, to }),
    enabled: Boolean(from && to),
    ...CACHE_TIMES.REALTIME,
  });
}

export function usePrinterOrder(scope: PortalScope, id: string | null) {
  return useQuery({
    queryKey: partnerPortalKeys.order(scope, id ?? ""),
    queryFn: () => printerPortalApi.getOrder(scope, id!),
    enabled: Boolean(id),
    ...CACHE_TIMES.REALTIME,
  });
}

/** Files are listed on open; nothing is downloaded until asked. */
export function usePrinterFiles(scope: PortalScope, id: string) {
  return useQuery({
    queryKey: partnerPortalKeys.files(scope, id),
    queryFn: () => printerPortalApi.getFiles(scope, id),
    ...CACHE_TIMES.STANDARD,
  });
}
